import { useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { login, ApiError } from "@/lib/api";
import { applyTheme } from "@/lib/theme";

const MODULES = [
  { icon: "fa-columns", label: "Board" },
  { icon: "fa-rocket", label: "Deployments" },
  { icon: "fa-folder-open", label: "Documents" },
  { icon: "fa-coins", label: "Finance" },
];

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Login is always light mode, regardless of the user's saved theme.
  // Restore their preference when leaving the page.
  useLayoutEffect(() => {
    document.body.classList.add("light-theme");
    return () => applyTheme();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      qc.setQueryData(["me"], user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col overflow-hidden lg:flex-row"
      style={{
        background:
          "radial-gradient(circle at 16% 20%, color-mix(in srgb, var(--color-accent) 9%, transparent), transparent 55%), radial-gradient(circle at 88% 82%, color-mix(in srgb, var(--color-accent) 6%, transparent), transparent 60%), var(--color-bg)",
      }}
    >
      {/* Brand panel — left on desktop, top on mobile */}
      <section className="login-rise relative z-10 flex flex-1 flex-col justify-between px-8 py-12 sm:px-12 lg:px-16 lg:py-16">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Vernias" className="h-9 w-9 object-contain" />
          <span className="text-xl font-semibold tracking-tight text-text-heading">
            Vernias Portal
          </span>
        </div>

        <div className="max-w-xl">
          <div
            className="mb-6 inline-flex items-center gap-2.5 text-[0.65rem] font-medium uppercase tracking-[0.18em]"
            style={{ color: "var(--color-accent)" }}
          >
            <span className="inline-block h-px w-6" style={{ background: "var(--color-accent)" }} />
            Internal platform
          </div>
          <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-text-heading sm:text-5xl lg:text-6xl">
            Everything we run,{" "}
            <span className="font-serif text-[1.05em] italic leading-[1.1]">in one place.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-text-secondary sm:text-lg">
            Projects, deployments, documents and the books for the Vernias team.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-2.5">
            {MODULES.map((m) => (
              <span
                key={m.label}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-card/60 px-3 py-1.5 text-xs font-medium text-text-secondary"
              >
                <i className={`fa-solid ${m.icon} text-[11px] text-accent`} />
                {m.label}
              </span>
            ))}
          </div>
        </div>

        <div className="text-[0.7rem] text-text-muted">
          © {new Date().getFullYear()} Vernias · Internal use only.
        </div>
      </section>

      {/* Auth panel — right on desktop, bottom on mobile */}
      <section className="relative z-10 flex w-full items-center justify-center px-6 pb-12 sm:px-10 lg:w-2/5 lg:py-16">
        <div className="login-rise w-full max-w-[400px] rounded-token-lg border border-border bg-bg-card p-7 shadow-token-lg">
          <h2 className="font-display text-xl font-semibold text-text-heading">Sign in</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Enter your credentials to access the portal.
          </p>

          <form onSubmit={onSubmit} className="mt-6" autoComplete="off">
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-text-muted">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@vernias.com"
              className="mb-4 w-full rounded-token-md border border-border bg-bg px-3 py-2.5 text-sm text-text outline-none transition-colors placeholder:text-text-muted/60 focus:border-accent focus:ring-2 focus:ring-accent/25"
            />

            <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-text-muted">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-token-md border border-border bg-bg px-3 py-2.5 text-sm text-text outline-none transition-colors placeholder:text-text-muted/60 focus:border-accent focus:ring-2 focus:ring-accent/25"
            />

            {error && (
              <p className="mt-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-token-md bg-accent text-on-accent py-2.5 text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.99] disabled:opacity-60"
              style={{ color: "var(--color-on-accent)" }}
            >
              {busy ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
